import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DriveFolder, uploadRedactedFileToGoogleDrive } from '../services/googleDriveService';
import {
  DefaultDriveFolder,
  dismissDefaultFolderSuggestion,
  getDefaultDriveFolder,
  setDefaultDriveFolder,
  shouldSuggestDefaultFolder,
  subscribeToPreferencesChanges,
} from '../utils/userPreferences';
import {
  DOC_TYPE_OPTIONS,
  DocTypeOption,
  buildRedactedFilename,
  getCurrentYearMonth,
  getFileExtension,
} from '../utils/postRedactionNaming';
import { createPdfFromText } from '../utils/pdfExport';
import {
  PAGE_INTEGRITY_USER_MESSAGE,
  validatePageIntegrity,
} from '../utils/pageIntegrity';
import DriveFolderPickerModal from './DriveFolderPickerModal';
import { Sentry } from '../lib/sentry';

const RECENT_CODES_KEY = 'redact_ai_recent_student_codes';
const RECENT_CODES_LIMIT = 10;

interface PostRedactionSaveModalProps {
  isOpen: boolean;
  sourceFileName: string;
  redactedText: string;
  onClose: () => void;
  /** When set (e.g. DOC from Smart mode), upload/download uses this blob instead of plain text */
  uploadBlob?: Blob | null;
  /** Override inferred extension from source filename (needed when export format differs from source, e.g. PDF → TXT) */
  outputExtension?: string | null;
  /** Opens Settings so the user can set a default Drive folder */
  onOpenSettings?: () => void;
  /** Original PDF page count for integrity validation before download */
  expectedPageCount?: number;
  /** Fired after a successful local download or Drive save (privacy-safe analytics hook) */
  onExportComplete?: () => void;
}

const readRecentCodes = (): string[] => {
  try {
    const raw = localStorage.getItem(RECENT_CODES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => typeof item === 'string' && item.trim().length > 0).slice(0, RECENT_CODES_LIMIT);
  } catch {
    return [];
  }
};

const saveRecentCode = (code: string) => {
  const trimmed = code.trim();
  if (!trimmed) return;
  const current = readRecentCodes().filter((existing) => existing !== trimmed);
  const next = [trimmed, ...current].slice(0, RECENT_CODES_LIMIT);
  localStorage.setItem(RECENT_CODES_KEY, JSON.stringify(next));
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatSavePath = (folderName: string, studentCode: string) =>
  `${folderName} / ${studentCode} /`;

const PostRedactionSaveModal: React.FC<PostRedactionSaveModalProps> = ({
  isOpen,
  sourceFileName,
  redactedText,
  onClose,
  uploadBlob = null,
  outputExtension = null,
  onOpenSettings,
  expectedPageCount,
  onExportComplete,
}) => {
  const { user } = useAuth();
  const [studentCode, setStudentCode] = useState('');
  const [docType, setDocType] = useState<DocTypeOption>('FIE');
  const [yearMonth, setYearMonth] = useState(getCurrentYearMonth());
  const [recentCodes, setRecentCodes] = useState<string[]>([]);
  const [showRecentDropdown, setShowRecentDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [driveMessage, setDriveMessage] = useState<string | null>(null);
  const [driveLink, setDriveLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [defaultFolder, setDefaultFolderState] = useState<DefaultDriveFolder | null>(null);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<'first-save' | 'change-default'>('first-save');
  const [showDefaultTip, setShowDefaultTip] = useState(false);
  const [integrityError, setIntegrityError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const extension = useMemo(() => {
    if (outputExtension && outputExtension.trim().length > 0) {
      return outputExtension.trim().replace(/^\./, '').toLowerCase();
    }
    if (uploadBlob) {
      return getFileExtension(sourceFileName);
    }
    return 'pdf';
  }, [outputExtension, sourceFileName, uploadBlob]);
  const generatedFileName = useMemo(
    () =>
      buildRedactedFilename(
        (studentCode.trim() || 'STUDENT-CODE').toUpperCase(),
        docType,
        yearMonth || getCurrentYearMonth(),
        extension
      ),
    [studentCode, docType, yearMonth, extension]
  );

  const canConfirm = studentCode.trim().length > 0 && yearMonth.trim().length === 7;
  const hasDefaultFolder = Boolean(defaultFolder);
  const savePathPreview =
    defaultFolder && studentCode.trim()
      ? formatSavePath(defaultFolder.name, studentCode.trim().toUpperCase())
      : defaultFolder
        ? formatSavePath(defaultFolder.name, '…')
        : null;

  useEffect(() => {
    if (!isOpen) return;
    const codes = readRecentCodes();
    setRecentCodes(codes);
    setStudentCode(codes[0] || '');
    setDocType('FIE');
    setYearMonth(getCurrentYearMonth());
    setDriveMessage(null);
    setDriveLink(null);
    setCopySuccess(false);
    setShowFolderPicker(false);
    setIntegrityError(null);
    setDefaultFolderState(getDefaultDriveFolder(user?.id));
    setShowDefaultTip(shouldSuggestDefaultFolder(user?.id));
  }, [isOpen, user?.id]);

  useEffect(() => {
    if (!isOpen) return;
    return subscribeToPreferencesChanges(() => {
      setDefaultFolderState(getDefaultDriveFolder(user?.id));
      setShowDefaultTip(shouldSuggestDefaultFolder(user?.id));
    });
  }, [isOpen, user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowRecentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const normalizedCode = studentCode.trim().toUpperCase();
  const finalFileName = buildRedactedFilename(normalizedCode, docType, yearMonth, extension);

  const buildExportBlob = (): { blob: Blob; ok: boolean } => {
    if (uploadBlob) {
      return { blob: uploadBlob, ok: true };
    }
    if (extension === 'txt') {
      if (expectedPageCount && expectedPageCount > 0) {
        const integrity = validatePageIntegrity(redactedText, expectedPageCount);
        if (!integrity.isComplete) {
          Sentry.captureMessage('export_page_integrity_mismatch', {
            level: 'error',
            tags: { error_stage: 'output' },
            extra: {
              original_page_count: integrity.originalPageCount,
              processed_page_count: integrity.processedPageCount,
              missing_page_count: integrity.missingPageNumbers.length,
              missing_pages: integrity.missingPageNumbers.slice(0, 50),
              export_format: 'txt',
            },
          });
          setIntegrityError(PAGE_INTEGRITY_USER_MESSAGE);
          return { blob: new Blob(), ok: false };
        }
      }
      return {
        blob: new Blob([redactedText], { type: 'text/plain;charset=utf-8' }),
        ok: true,
      };
    }

    const exported = createPdfFromText(redactedText, {
      expectedPageCount: expectedPageCount && expectedPageCount > 0 ? expectedPageCount : undefined,
    });
    if (exported.integrity && !exported.integrity.isComplete) {
      Sentry.captureMessage('export_page_integrity_mismatch', {
        level: 'error',
        tags: { error_stage: 'output' },
        extra: {
          original_page_count: exported.integrity.originalPageCount,
          processed_page_count: exported.integrity.processedPageCount,
          missing_page_count: exported.integrity.missingPageNumbers.length,
          missing_pages: exported.integrity.missingPageNumbers.slice(0, 50),
          logical_page_count: exported.logicalPageCount,
          physical_page_count: exported.physicalPageCount,
          output_file_size: exported.blob.size,
          export_format: 'pdf',
        },
      });
      setIntegrityError(PAGE_INTEGRITY_USER_MESSAGE);
      return { blob: new Blob(), ok: false };
    }
    setIntegrityError(null);
    return { blob: exported.blob, ok: true };
  };

  const handleDownloadOnly = () => {
    if (!canConfirm) return;
    const { blob, ok } = buildExportBlob();
    if (!ok) return;
    saveRecentCode(normalizedCode);
    downloadBlob(blob, finalFileName);
    onExportComplete?.();
    onClose();
  };

  const uploadToDefaultFolder = async (parent: DefaultDriveFolder) => {
    setIsSaving(true);
    setDriveMessage(null);
    try {
      const { blob, ok } = buildExportBlob();
      if (!ok) return;
      saveRecentCode(normalizedCode);
      const upload = await uploadRedactedFileToGoogleDrive({
        studentCode: normalizedCode,
        fileName: finalFileName,
        fileBlob: blob,
        parentFolderId: parent.id,
        parentFolderName: parent.name,
      });
      setDriveMessage(`Saved to Google Drive: ${upload.folderPath}`);
      setDriveLink(upload.webViewLink);
      setRecentCodes(readRecentCodes());
      onExportComplete?.();
    } catch (error) {
      setDriveMessage(error instanceof Error ? error.message : 'Failed to save file to Google Drive.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveToGoogleDrive = async () => {
    if (!canConfirm) return;

    if (defaultFolder) {
      await uploadToDefaultFolder(defaultFolder);
      return;
    }

    // First time: pick root folder, then save under student subfolder
    setPickerMode('first-save');
    setShowFolderPicker(true);
  };

  const handleFolderPicked = async (folder: DriveFolder) => {
    setShowFolderPicker(false);

    // Persist as default so they never have to pick again
    setDefaultDriveFolder(user?.id, folder);
    setDefaultFolderState(folder);
    setShowDefaultTip(false);

    if (pickerMode === 'change-default') {
      setDriveMessage(`Default folder updated to “${folder.name}”.`);
      return;
    }

    // First-time save: upload now that default is set
    await uploadToDefaultFolder(folder);
  };

  const handleChangeDefaultFolder = () => {
    setPickerMode('change-default');
    setShowFolderPicker(true);
  };

  const handleCopyLink = async () => {
    if (!driveLink) return;
    await navigator.clipboard.writeText(driveLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1800);
  };

  const handleDismissTip = () => {
    dismissDefaultFolderSuggestion(user?.id);
    setShowDefaultTip(false);
  };

  const handleOpenSettingsFromTip = () => {
    dismissDefaultFolderSuggestion(user?.id);
    setShowDefaultTip(false);
    onOpenSettings?.();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full h-full sm:h-auto sm:max-w-xl sm:mx-4 bg-white dark:bg-slate-800 sm:rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-6 py-5" style={{ backgroundColor: '#15452f' }}>
            <h2 className="text-2xl font-bold text-white">Save Your Prepared Document</h2>
            <p className="text-emerald-100 text-sm mt-1">
              AI-ready copy — personally identifiable information has been redacted from this file.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div className="relative" ref={dropdownRef}>
              <label htmlFor="student-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Student Code (e.g. MR-7291-G5-HART)
              </label>
              <input
                id="student-code"
                type="text"
                value={studentCode}
                onChange={(e) => setStudentCode(e.target.value)}
                onFocus={() => setShowRecentDropdown(true)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="MR-7291-G5-HART"
              />
              {showRecentDropdown && recentCodes.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-44 overflow-y-auto">
                  {recentCodes.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => {
                        setStudentCode(code);
                        setShowRecentDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      {code}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="doc-type" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Document Type
              </label>
              <select
                id="doc-type"
                value={docType}
                onChange={(e) => setDocType(e.target.value as DocTypeOption)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {DOC_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="year-month" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Date (YYYY-MM)
              </label>
              <input
                id="year-month"
                type="month"
                value={yearMonth}
                onChange={(e) => setYearMonth(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Filename Preview</p>
              <p className="mt-1 text-sm font-medium text-slate-800 dark:text-slate-100 break-all">{generatedFileName}</p>
              {!uploadBlob && extension === 'pdf' && (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Exports as a readable prepared PDF (redacted AI-ready copy).
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleDownloadOnly}
                disabled={!canConfirm || isSaving}
                className="py-3 px-4 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 font-semibold hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Download Prepared PDF
              </button>
              <button
                type="button"
                onClick={handleSaveToGoogleDrive}
                disabled={!canConfirm || isSaving}
                className="py-3 px-4 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save to Google Drive'}
              </button>
            </div>

            {hasDefaultFolder && savePathPreview && (
              <div className="-mt-1 space-y-0.5">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Saving to:{' '}
                  <span className="font-medium text-slate-700 dark:text-slate-200">{savePathPreview}</span>
                </p>
                <button
                  type="button"
                  onClick={handleChangeDefaultFolder}
                  disabled={isSaving}
                  className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-50"
                >
                  Change default folder
                </button>
              </div>
            )}

            {!hasDefaultFolder && (
              <p className="text-xs text-slate-500 dark:text-slate-400 -mt-1">
                First save: you&apos;ll choose a root folder (e.g. My Caseload). Student subfolders are created automatically.
              </p>
            )}

            {showDefaultTip && !hasDefaultFolder && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-sm text-amber-900 dark:text-amber-100 flex items-start justify-between gap-3">
                <p>
                  Tip: Set a default folder in Settings to auto-organize by student.
                  {onOpenSettings && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={handleOpenSettingsFromTip}
                        className="font-semibold underline hover:no-underline"
                      >
                        Open Settings
                      </button>
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleDismissTip}
                  className="text-amber-700 dark:text-amber-300 hover:opacity-80 flex-shrink-0"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}

            {integrityError && (
              <div className="rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
                {integrityError}
              </div>
            )}

            {driveMessage && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm text-emerald-800 dark:text-emerald-200">
                {driveMessage}
              </div>
            )}

            {driveLink && (
              <div className="space-y-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href={driveLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium hover:opacity-90"
                  >
                    Open in Google Drive
                  </a>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600"
                  >
                    {copySuccess ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Ready for an AI tool? Copy the file link and upload it to your preferred generative AI platform or assistant (for example, a notebook or project workspace).
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Review the prepared document first. Do not upload original records that still contain student PII.
                </p>
              </div>
            )}
          </div>

          <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <DriveFolderPickerModal
        isOpen={showFolderPicker}
        title={
          pickerMode === 'change-default'
            ? 'Choose default save folder'
            : 'Choose your default save folder'
        }
        onClose={() => setShowFolderPicker(false)}
        onSelect={handleFolderPicked}
      />
    </>
  );
};

export default PostRedactionSaveModal;
